import React, { useState, useEffect } from 'react';
import { fetchStaffProfiles, updateStaffProfile, createStaffProfile } from '../api';

const ROLE_LABELS = {
  OWNER: 'Owner',
  ATTORNEY: 'Attorney',
  ASSOCIATE: 'Associate',
  PARALEGAL: 'Paralegal',
  SECRETARY: 'Secretary',
  ACCOUNTANT: 'Accountant',
  AUDITOR: 'Auditor',
};

const ROLE_OPTIONS = ['PARTNER', 'ASSOCIATE', 'PARALEGAL', 'SUPPORT_STAFF', 'ACCOUNTANT', 'CANDIDATE_ATTORNEY'];

const EmployeeList = ({ authAxios, addToast, onSelectEmployee }) => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ username: '', email: '', first_name: '', last_name: '', role: 'ASSOCIATE', basic_salary: '' });
  const [addSaving, setAddSaving] = useState(false);

  const loadProfiles = async () => {
    try {
      const data = await fetchStaffProfiles(authAxios);
      setProfiles(data.results || data);
    } catch {
      addToast('Failed to load staff profiles', 'error');
    }
    setLoading(false);
  };

  useEffect(() => { loadProfiles(); }, [authAxios, addToast]);

  const handleAddStaff = async () => {
    if (!addForm.username || !addForm.email) {
      addToast('Username and email are required', 'error');
      return;
    }
    setAddSaving(true);
    try {
      await createStaffProfile(authAxios, addForm);
      addToast('Staff member created successfully', 'success');
      setShowAddModal(false);
      setAddForm({ username: '', email: '', first_name: '', last_name: '', role: 'ASSOCIATE', basic_salary: '' });
      await loadProfiles();
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data?.username?.[0] || 'Failed to create staff member';
      addToast(msg, 'error');
    }
    setAddSaving(false);
  };

  const filtered = profiles.filter(p => {
    const name = `${p.user_first_name || ''} ${p.user_last_name || ''} ${p.username || ''}`.toLowerCase();
    return name.includes(search.toLowerCase());
  });

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Employees</h2>
        <div className="flex gap-3 items-center">
          <input
            type="text"
            placeholder="Search employees..."
            className="border rounded p-2 w-64"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 whitespace-nowrap"
          >
            + Add Staff Member
          </button>
        </div>
      </div>

      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-bold mb-4">Add Staff Member</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Username *</label>
                <input type="text" className="border rounded p-2 w-full" value={addForm.username} onChange={e => setAddForm({ ...addForm, username: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Email *</label>
                <input type="email" className="border rounded p-2 w-full" value={addForm.email} onChange={e => setAddForm({ ...addForm, email: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">First Name</label>
                  <input type="text" className="border rounded p-2 w-full" value={addForm.first_name} onChange={e => setAddForm({ ...addForm, first_name: e.target.value })} />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Last Name</label>
                  <input type="text" className="border rounded p-2 w-full" value={addForm.last_name} onChange={e => setAddForm({ ...addForm, last_name: e.target.value })} />
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Role</label>
                <select className="border rounded p-2 w-full" value={addForm.role} onChange={e => setAddForm({ ...addForm, role: e.target.value })}>
                  {ROLE_OPTIONS.map(r => <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Basic Salary (BWP)</label>
                <input type="number" step="0.01" className="border rounded p-2 w-full" value={addForm.basic_salary} onChange={e => setAddForm({ ...addForm, basic_salary: e.target.value })} />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setShowAddModal(false)} className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">Cancel</button>
              <button disabled={addSaving} onClick={handleAddStaff} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
                {addSaving ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-center py-8">Loading...</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(profile => (
            <div
              key={profile.id}
              className="border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => onSelectEmployee(profile.id)}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold">
                  {(profile.user_first_name || profile.username || '?')[0].toUpperCase()}
                </div>
                <div>
                  <div className="font-semibold">
                    {`${profile.user_first_name || ''} ${profile.user_last_name || ''}`.trim() || profile.username}
                  </div>
                  <div className="text-xs text-gray-500">{ROLE_LABELS[profile.role] || profile.role}</div>
                </div>
              </div>
              <div className="mt-3 text-sm text-gray-600 space-y-1">
                {profile.basic_salary && (
                  <div>Salary: P {parseFloat(profile.basic_salary).toLocaleString('en', { minimumFractionDigits: 2 })}</div>
                )}
                {profile.bank_name && <div>Bank: {profile.bank_name}</div>}
                {profile.bank_account_number_masked && <div>Account: {profile.bank_account_number_masked}</div>}
              </div>
            </div>
          ))}
          {filtered.length === 0 && <p className="col-span-3 text-center text-gray-500 py-8">No employees found</p>}
        </div>
      )}
    </div>
  );
};

const EmployeeProfile = ({ authAxios, addToast, profileId, onBack }) => {
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await fetchStaffProfiles(authAxios, { id: profileId });
        const list = data.results || data;
        const p = Array.isArray(list) ? list.find(x => x.id === profileId) || list[0] : list;
        setProfile(p);
        setForm({
          basic_salary: p.basic_salary || '',
          bank_name: p.bank_name || '',
          bank_branch_code: p.bank_branch_code || '',
          id_number: p.id_number || '',
          tax_pin: p.tax_pin || '',
        });
      } catch {
        addToast('Failed to load profile', 'error');
      }
    };
    load();
  }, [authAxios, profileId, addToast]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await updateStaffProfile(authAxios, profileId, form);
      setProfile({ ...profile, ...updated });
      setEditing(false);
      addToast('Profile updated', 'success');
    } catch {
      addToast('Failed to update profile', 'error');
    }
    setSaving(false);
  };

  if (!profile) return <div className="p-8 text-center">Loading profile...</div>;

  const name = `${profile.user_first_name || ''} ${profile.user_last_name || ''}`.trim() || profile.username;

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold">{name}</h2>
          <span className="text-sm text-gray-500">{ROLE_LABELS[profile.role] || profile.role}</span>
        </div>
        <div className="flex gap-2">
          {!editing ? (
            <button onClick={() => setEditing(true)} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">Edit</button>
          ) : (
            <>
              <button disabled={saving} onClick={handleSave} className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50">Save</button>
              <button onClick={() => setEditing(false)} className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">Cancel</button>
            </>
          )}
          {onBack && <button onClick={onBack} className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">Back</button>}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <h3 className="font-semibold text-lg border-b pb-2">Personal Information</h3>
          <div>
            <label className="block text-sm text-gray-600">Email</label>
            <div className="text-sm">{profile.user_email || '—'}</div>
          </div>
          <div>
            <label className="block text-sm text-gray-600">ID Number</label>
            {editing ? (
              <input type="text" className="border rounded p-2 w-full" value={form.id_number} onChange={e => setForm({ ...form, id_number: e.target.value })} />
            ) : (
              <div className="text-sm">{profile.id_number || '—'}</div>
            )}
          </div>
          <div>
            <label className="block text-sm text-gray-600">Tax PIN</label>
            {editing ? (
              <input type="text" className="border rounded p-2 w-full" value={form.tax_pin} onChange={e => setForm({ ...form, tax_pin: e.target.value })} />
            ) : (
              <div className="text-sm">{profile.tax_pin || '—'}</div>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="font-semibold text-lg border-b pb-2">Payroll & Banking</h3>
          <div>
            <label className="block text-sm text-gray-600">Basic Salary (BWP)</label>
            {editing ? (
              <input type="number" step="0.01" className="border rounded p-2 w-full" value={form.basic_salary} onChange={e => setForm({ ...form, basic_salary: e.target.value })} />
            ) : (
              <div className="text-sm">{profile.basic_salary ? `P ${parseFloat(profile.basic_salary).toLocaleString('en', { minimumFractionDigits: 2 })}` : '—'}</div>
            )}
          </div>
          <div>
            <label className="block text-sm text-gray-600">Bank Name</label>
            {editing ? (
              <input type="text" className="border rounded p-2 w-full" value={form.bank_name} onChange={e => setForm({ ...form, bank_name: e.target.value })} />
            ) : (
              <div className="text-sm">{profile.bank_name || '—'}</div>
            )}
          </div>
          <div>
            <label className="block text-sm text-gray-600">Branch Code</label>
            {editing ? (
              <input type="text" className="border rounded p-2 w-full" value={form.bank_branch_code} onChange={e => setForm({ ...form, bank_branch_code: e.target.value })} />
            ) : (
              <div className="text-sm">{profile.bank_branch_code || '—'}</div>
            )}
          </div>
          <div>
            <label className="block text-sm text-gray-600">Account Number</label>
            <div className="text-sm">{profile.bank_account_number_masked || '—'}</div>
            <p className="text-xs text-gray-400 mt-1">Encrypted — only masked view available</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export { EmployeeList, EmployeeProfile };
export default EmployeeList;
